import { useState, useEffect, useRef, useCallback } from 'react';
import imageCacheService from '../services/imageCache';

interface UseCachedImageUrlResult {
  cachedUrl: string;
  isLoading: boolean;
  error: string | null;
  onImageError: () => void; // Call this when img onError fires to fallback to original URL
}

/**
 * Custom hook to get a cached blob URL for an image.
 *
 * On first render, if the image is already cached, returns the blob URL immediately.
 * Otherwise, returns the original URL and fetches the image in the background.
 * Once fetched, updates to the blob URL.
 *
 * If the blob URL becomes invalid (e.g., after LRU eviction), calling onImageError
 * will fallback to the original URL.
 *
 * @param imageId - Unique image ID for cache key
 * @param originalUrl - The S3 presigned URL
 * @returns Object with cachedUrl, isLoading, error, and onImageError callback
 */
export function useCachedImageUrl(
  imageId: number,
  originalUrl: string
): UseCachedImageUrlResult {
  const [cachedUrl, setCachedUrl] = useState<string>(() => {
    // Synchronously check cache on initial render
    const cached = imageCacheService.get(imageId);
    return cached || originalUrl;
  });
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    return !imageCacheService.has(imageId);
  });
  const [error, setError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const mountedRef = useRef(true);

  // Callback to handle image load errors (e.g., revoked blob URL)
  const onImageError = useCallback(() => {
    if (cachedUrl !== originalUrl) {
      // Blob URL might be invalid, fallback to original
      setUseFallback(true);
      setCachedUrl(originalUrl);
    }
  }, [cachedUrl, originalUrl]);

  useEffect(() => {
    mountedRef.current = true;

    // If using fallback, don't try to re-cache
    if (useFallback) {
      return;
    }

    // Already cached
    if (imageCacheService.has(imageId)) {
      const cached = imageCacheService.get(imageId);
      if (cached) {
        setCachedUrl(cached);
        setIsLoading(false);
      }
      return;
    }

    // Fetch and cache in background
    let cancelled = false;

    setIsLoading(true);
    imageCacheService
      .fetchAndCache(imageId, originalUrl)
      .then((blobUrl) => {
        if (!cancelled && mountedRef.current) {
          setCachedUrl(blobUrl);
          setIsLoading(false);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled && mountedRef.current) {
          // Keep using original URL on error
          setError(err.message || 'Failed to cache image');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [imageId, originalUrl, useFallback]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { cachedUrl, isLoading, error, onImageError };
}
